import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { H3, RichTextParagraph, RichH2, RichP1 } from '../../components/Typography';
import { isFieldValid } from '../../utils/validation';
import { CTALink } from '../../components/Link';
import { isRightAligned, isLeftAligned } from '../../utils/variations';
import { BackgroundImage } from '../Image';
import { Transition } from '../ComponentWrappers';
import { OnScrollWrapper } from '../UtilityWrappers';
import media from '../../utils/media';
import { detectEscape, setYearFocus, useMobile } from './Timeline._helpers';
import { SCREEN_WIDTH } from '../../utils/media';
import { ScrollDownIcon } from '../Icon';
import { getParsedField, getFieldValue } from '../../utils/helpers';
const Container = styled.section `
  position: relative;
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  padding: 50px 100px;
  background-color: ${({ bgColor }) => (bgColor ? bgColor : 'white')};
  display: flex;
  flex-direction: ${({ alignment }) => (isRightAligned(alignment) ? 'row-reverse' : 'row')};
  justify-content: center;
  align-items: center;

  ${media.tablet} {
    flex-direction: ${({ alignment }) => (isRightAligned(alignment) ? 'column-reverse' : 'column')};
    align-items: flex-start;
    justify-content: center;
    padding: 50px 100px;
  }

  ${media.smallTablet} {
    padding: 30px 60px;
  }

  ${media.mobile} {
    padding: 0px;
    min-width: 100vw;
  }
`;
const ContainerIcon = styled.div `
  position: absolute;
  left: 50%;
  margin-left: -17px;
  bottom: 30px;

  ${media.tablet} {
    display: none;
  }
`;
const TextContainer = styled.div `
  width: 100%;
  max-width: 540px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  *:last-child {
    margin-bottom: 0px;
  }

  &:focus {
    outline: none;
  }

  ${media.mobileL} {
    padding: 20px 30px 0px;
  }
`;
// ------ extended to SlideVideo
export const YearLabel = styled(H3) `
  ${media.mobile} {
    font-size: 16px;
    line-height: 22px;
  }
`;
export const ContentSubtitle = styled(RichTextParagraph) `
  color: ${({ theme }) => theme.secondaryRedDark};
  font-family: ${({ theme }) => theme.fontFamilyHeading};
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.02em;
  margin-bottom: 10px;

  ${media.mobile} {
    font-size: 14px;
    line-height: 20px;
  }
`;
export const ContentTitle = styled(RichH2) `
  width: 100%;
  ${media.mobile} {
    font-family: ${({ theme }) => theme.fontFamilyBold};
    font-size: 22px;
    line-height: 25px;
    margin-bottom: 2px;
  }
`;
export const ContentBodyText = styled(RichP1) `
  width: 100%;
  margin-bottom: 15px;

  ${media.mobile} {
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 10px;
  }
`;
// ------ extended to SlideVideo
const ImageContainer = styled.div `
  height: 600px;
  width: 540px;

  ${media.tablet} {
    width: 100%;
    max-height: 450px;
  }

  ${media.mobile} {
    height: 200px;
    max-width: 320px;
    margin: 0 auto;
  }
`;
const ForegroundImage = styled(BackgroundImage) `
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
`;
const Divider = styled.div `
  height: 100%;
  width: 30px;

  ${media.tablet} {
    height: 40px;
    width: 100%;
  }

  ${media.mobile} {
    height: 18px;
  }
`;
const TimelineSlideImage = ({ fields, years, contentWidth, index, }) => {
    const { contentAlignment, backgroundColor, contentSubtitle, contentTitle, contentBodyText, ctaLink, ctaLinkText, ctaType, desktopImage, mobileImage, ariaLabel, } = fields;
    const [isComponentVisible, setIsComponentVisible] = useState(false);
    const [setEscape, setSetEscape] = useState(false);
    const bg = backgroundColor.value;
    const color = '#'.concat(bg.split('-')[1]);
    const parsedTitle = getParsedField(contentTitle);
    const parsedSubtitle = getParsedField(contentSubtitle);
    const parsedBodyText = getParsedField(contentBodyText);
    const alignment = getFieldValue(contentAlignment);
    const escapeCheck = (evt) => {
        if (setEscape && detectEscape(evt)) {
            evt.preventDefault();
            setYearFocus(years.id || '');
        }
    };
    useEffect(() => {
        const isMobile = useMobile(contentWidth || 0, SCREEN_WIDTH.MOBILE) || false;
        setSetEscape(isMobile);
    }, []);
    return (React.createElement(Container, { "data-decade": years.decade, "data-year": years.year, "data-id": years.id, bgColor: color, alignment: alignment, index: index, onKeyDown: escapeCheck, "aria-label": ariaLabel && ariaLabel.value },
        React.createElement(TextContainer, null,
            !!years.year && React.createElement(YearLabel, { field: { value: years.year } }),
            isFieldValid(contentSubtitle) && React.createElement(ContentSubtitle, { field: parsedSubtitle }),
            isFieldValid(contentTitle) && React.createElement(ContentTitle, { field: parsedTitle }),
            isFieldValid(contentBodyText) && React.createElement(ContentBodyText, { field: parsedBodyText }),
            isFieldValid(ctaLink, 'href') && isFieldValid(ctaLinkText) && (React.createElement(CTALink, { field: ctaLink, textField: ctaLinkText, typeField: ctaType }))),
        React.createElement(Divider, null),
        React.createElement(OnScrollWrapper, { onVisibilityChange: setIsComponentVisible, isPartiallyVisible: true },
            React.createElement(ImageContainer, null, isFieldValid(desktopImage, 'src') && isComponentVisible && (React.createElement(Transition, { delay: 1000, initiateTransition: isComponentVisible, transitionType: isLeftAligned(alignment) ? 'slideInRight' : 'slideInLeft' },
                React.createElement(ForegroundImage, { alignment: alignment, desktopImage: desktopImage, mobileImage: mobileImage, "aria-label": getFieldValue(desktopImage, 'alt') }))))),
        React.createElement(ContainerIcon, null,
            React.createElement(ScrollDownIcon, null))));
};
export default TimelineSlideImage;
